import { Skeleton } from '@/components/ui/skeleton';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';

export type LoadingVariant =
  | 'page'
  | 'card'
  | 'list'
  | 'grid'
  | 'detail'
  | 'spinner'
  | 'minimal'
  | 'hero'
  | 'table';

interface LoadingFallbackProps {
  variant?: LoadingVariant;
  count?: number;
  className?: string;
  message?: string;
  columns?: number;
}

const SpinnerLoader = ({ message, className }: { message?: string; className?: string }) => (
  <div className={cn("flex flex-col items-center justify-center py-12", className)}>
    <Loader2 className="h-8 w-8 animate-spin text-primary mb-4" />
    <p className="text-muted-foreground">{message || 'A carregar...'}</p>
  </div>
);

const MinimalLoader = ({ className }: { className?: string }) => (
  <div className={cn("flex items-center justify-center py-4", className)}>
    <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
  </div>
);

const CardSkeleton = () => (
  <Card className="overflow-hidden">
    <Skeleton className="h-48 w-full rounded-none" />
    <CardHeader className="space-y-2">
      <Skeleton className="h-5 w-3/4" />
      <Skeleton className="h-4 w-full" />
      <Skeleton className="h-4 w-2/3" />
    </CardHeader>
    <CardContent>
      <Skeleton className="h-10 w-full" />
    </CardContent>
  </Card>
);

const ListItemSkeleton = () => (
  <div className="flex items-center gap-4 p-4 border rounded-lg">
    <Skeleton className="h-12 w-12 rounded-full" />
    <div className="flex-1 space-y-2">
      <Skeleton className="h-4 w-1/3" />
      <Skeleton className="h-3 w-1/2" />
    </div>
    <Skeleton className="h-8 w-20" />
  </div>
);

const HeroSkeleton = () => (
  <div className="relative w-full">
    <Skeleton className="h-[400px] w-full" />
    <div className="absolute inset-0 flex items-center justify-center">
      <div className="text-center space-y-4 max-w-2xl px-4">
        <Skeleton className="h-12 w-3/4 mx-auto" />
        <Skeleton className="h-6 w-full mx-auto" />
        <Skeleton className="h-6 w-2/3 mx-auto" />
        <div className="flex gap-4 justify-center pt-4">
          <Skeleton className="h-12 w-32" />
          <Skeleton className="h-12 w-32" />
        </div>
      </div>
    </div>
  </div>
);

const DetailSkeleton = () => (
  <div className="max-w-4xl mx-auto space-y-6 p-4">
    <Skeleton className="h-8 w-32" />
    <Skeleton className="h-12 w-3/4" />
    <div className="flex gap-2">
      <Skeleton className="h-6 w-20" />
      <Skeleton className="h-6 w-24" />
    </div>
    <Skeleton className="h-64 w-full rounded-lg" />
    <div className="space-y-4">
      <Skeleton className="h-4 w-full" />
      <Skeleton className="h-4 w-5/6" />
      <Skeleton className="h-4 w-4/5" />
      <Skeleton className="h-4 w-3/4" />
    </div>
    <div className="grid md:grid-cols-2 gap-4">
      <Skeleton className="h-24 w-full rounded-lg" />
      <Skeleton className="h-24 w-full rounded-lg" />
    </div>
  </div>
);

const PageSkeleton = () => (
  <div className="container py-8 space-y-8">
    {/* Header */}
    <div className="text-center space-y-4">
      <Skeleton className="h-10 w-1/3 mx-auto" />
      <Skeleton className="h-5 w-2/3 mx-auto" />
    </div>

    {/* Content grid */}
    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
      {[...Array(6)].map((_, i) => (
        <CardSkeleton key={i} />
      ))}
    </div>
  </div>
);

const TableSkeleton = ({ count = 5 }: { count?: number }) => (
  <div className="space-y-3">
    {/* Table header */}
    <div className="flex gap-4 p-3 bg-muted rounded-t-lg">
      <Skeleton className="h-4 w-1/4" />
      <Skeleton className="h-4 w-1/4" />
      <Skeleton className="h-4 w-1/4" />
      <Skeleton className="h-4 w-1/4" />
    </div>
    {/* Table rows */}
    {[...Array(count)].map((_, i) => (
      <div key={i} className="flex gap-4 p-3 border-b">
        <Skeleton className="h-4 w-1/4" />
        <Skeleton className="h-4 w-1/4" />
        <Skeleton className="h-4 w-1/4" />
        <Skeleton className="h-4 w-1/4" />
      </div>
    ))}
  </div>
);

const LoadingFallback = ({
  variant = 'spinner',
  count = 4,
  className,
  message,
  columns = 3,
}: LoadingFallbackProps) => {
  switch (variant) {
    case 'spinner':
      return <SpinnerLoader message={message} className={className} />;

    case 'minimal':
      return <MinimalLoader className={className} />;

    case 'card':
      return (
        <div className={cn("p-4", className)}>
          <CardSkeleton />
        </div>
      );

    case 'grid':
      return (
        <div className={cn(
          `grid gap-6`,
          columns === 2 && 'md:grid-cols-2',
          columns === 3 && 'md:grid-cols-2 lg:grid-cols-3',
          columns === 4 && 'md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4',
          className
        )}>
          {[...Array(count)].map((_, i) => (
            <CardSkeleton key={i} />
          ))}
        </div>
      );

    case 'list':
      return (
        <div className={cn("space-y-4", className)}>
          {[...Array(count)].map((_, i) => (
            <ListItemSkeleton key={i} />
          ))}
        </div>
      );

    case 'detail':
      return <DetailSkeleton />;

    case 'page':
      return <PageSkeleton />;

    case 'hero':
      return <HeroSkeleton />;

    case 'table':
      return <TableSkeleton count={count} />;

    default:
      return <SpinnerLoader message={message} className={className} />;
  }
};

export default LoadingFallback;
